import React from 'react';
import type { ComponentProps } from 'react';

import { IconButton as UclIconButton, Input as UclInput } from '@rbilabs/universal-components';
import { StyleSheet } from 'react-native';
import type { DimensionValue } from 'react-native';

import { Box } from 'components/ui';
import type { BoxProps } from 'components/ui';
import { getDesignToken, spacing } from 'styles/constants';

import type { DisplayValueStyleProps } from './types';

export const Wrapper = ({ style, ...props }: BoxProps) =>
  React.createElement(Box, { ...props, style: [styles.wrapper, style] });

export const Input = UclInput.withConfig({
  height: 12,
  width: 12,
  margin: 0,
  borderWidth: getDesignToken('border-width-input'),
  borderRadius: getDesignToken('border-radius-input'),
  borderColor: 'transparent',
  backgroundColor: getDesignToken('background-input'),
  textAlign: 'center',
  color: getDesignToken('text-default'),

  _focus: {
    borderColor: getDesignToken('border-color-input'),
  },

  _disabled: {
    borderColor: 'transparent',
    color: getDesignToken('text-disabled'),
  },
});

export const DisplayedValue = ({
  $width,
  $disabled,
  $overrides,
  style,
  ...props
}: BoxProps & {
  $width?: DimensionValue;
  $disabled?: boolean;
  $overrides?: DisplayValueStyleProps;
}) =>
  React.createElement(Box, {
    ...props,
    focusable: true,
    style: [
      styles.displayedValue,
      {
        backgroundColor: $overrides?.background,
        height: resolveSpacingValue($overrides?.height),
        marginHorizontal: resolveSpacingValue($overrides?.marginX),
        minWidth: resolveSpacingValue($overrides?.minWidth ?? $width),
        width: resolveSpacingValue($overrides?.width),
      },
      $overrides?.style,
      style,
    ],
  });

export const IconButton = UclIconButton.withConfig<{
  disabled?: boolean;
  $overrides?: ComponentProps<typeof UclIconButton>;
}>(p => ({
  marginX: -3,
  _icon: {
    color: p.disabled ? getDesignToken('icon-disabled') : getDesignToken('icon-default'),
  },
  _pressed: {
    // Avoid overlapping with numeric input while pressed
    zIndex: 1,
  },
  ...p.$overrides,
}));

const styles = StyleSheet.create({
  displayedValue: {
    alignItems: 'center',
    backgroundColor: getDesignToken('background-input'),
    justifyContent: 'center',
    marginHorizontal: spacing.$2,
    marginVertical: 0,
    maxWidth: spacing.$20,
    minHeight: spacing.$12,
  },
  wrapper: {
    alignItems: 'center',
    flexDirection: 'row',
  },
});

const resolveSpacingValue = (
  value: DimensionValue | string | undefined
): DimensionValue | undefined =>
  typeof value === 'number'
    ? spacing[`$${value}` as keyof typeof spacing] ?? value
    : typeof value === 'string' && value.startsWith('$')
    ? spacing[value as keyof typeof spacing]
    : (value as DimensionValue | undefined);
